"use client"

import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { LogoMarquee } from "@/components/ui/logo-marquee"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"

type LogoTagKey = "partnerWith" | "experienceAt" | "asSeenOn"

const PARTNER_LOGOS: {
  src: string
  alt: string
  h: number
  tagKey: LogoTagKey
  href?: string
}[] = [
  {
    src: blobMediaUrl("/images/logos/unilabs.svg"),
    alt: "Unilabs",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/oxford.svg"),
    alt: "Oxford",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/google.svg"),
    alt: "Google",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/newzapiens.svg"),
    alt: "newzapiens",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/siemens.svg"),
    alt: "Siemens Healthineers",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/quote.svg"),
    alt: "QUOTE",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://www.quotenet.nl/zakelijk/a69976033/cody-gakpo-en-anderen-storten-6-miljoen-in-pre-scan-voor-millennials/",
  },
  {
    src: blobMediaUrl("/images/logos/tum.svg"),
    alt: "TUM",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/techeu.svg"),
    alt: "tech.eu",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://tech.eu/2026/01/13/ahead-health-raises-6m-to-build-ai-powered-health-system-in-europe/",
  },
  {
    src: blobMediaUrl("/images/logos/kaia.svg"),
    alt: "Kaia Health",
    h: 28,
    tagKey: "experienceAt",
  },
]

interface PartnerLogoMarqueeProps {
  theme?: "light" | "dark"
  className?: string
  /** Scroll duration in seconds (passthrough). Higher = slower. Default: 40. */
  duration?: number
  /** "marquee" (default) scrolls all logos; "static" renders a centered,
   *  non-animated row of the `only` subset. */
  layout?: "marquee" | "static"
  /** For the static layout: the logo `alt`s to show, in order. */
  only?: string[]
}

/**
 * Self-contained partner logo marquee — same logos as the homepage,
 * with translated hover labels ("Partner with", "As seen on", etc.).
 * Pass `layout="static"` + `only={[...]}` for a fixed, non-scrolling row.
 */
export function PartnerLogoMarquee({
  theme = "light",
  className,
  duration,
  layout = "marquee",
  only,
}: PartnerLogoMarqueeProps) {
  const t = useTranslations("home")

  if (layout === "static") {
    const logos = only
      ? only
          .map((alt) => PARTNER_LOGOS.find((l) => l.alt === alt))
          .filter((l): l is (typeof PARTNER_LOGOS)[number] => Boolean(l))
      : PARTNER_LOGOS
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-center gap-x-10 gap-y-6 pt-6 md:gap-x-16",
          className
        )}
      >
        {logos.map((l) => {
          const Wrapper = l.href ? "a" : "div"
          const wrapperProps = l.href
            ? { href: l.href, target: "_blank", rel: "noopener noreferrer" }
            : {}
          return (
            <Wrapper
              key={l.alt}
              {...wrapperProps}
              className={cn(
                "group relative flex flex-col items-center",
                l.href ? "cursor-pointer" : "cursor-default"
              )}
            >
              {/* hover label above the logo (Partner with / As seen on / …) */}
              <span
                className={cn(
                  "type-label-sm pointer-events-none absolute -top-6 whitespace-nowrap opacity-0 transition-[opacity,transform] duration-150 group-hover:translate-y-0 group-hover:opacity-100",
                  theme === "dark" ? "text-white/60" : "text-neutral-500"
                )}
              >
                {t(l.tagKey)}
              </span>
              <Image
                src={l.src}
                alt={l.alt}
                width={100}
                height={32}
                style={{ height: l.h }}
                className={cn(
                  "w-auto shrink-0 object-contain transition-[opacity,filter] duration-150",
                  theme === "dark"
                    ? "opacity-55 [filter:brightness(0)_invert(1)] group-hover:opacity-80"
                    : "opacity-65 grayscale group-hover:opacity-100 group-hover:grayscale-0"
                )}
              />
            </Wrapper>
          )
        })}
      </div>
    )
  }

  return (
    <LogoMarquee
      items={PARTNER_LOGOS.map((l) => ({
        src: l.src,
        alt: l.alt,
        h: l.h,
        href: l.href,
        label: t(l.tagKey),
      }))}
      theme={theme}
      duration={duration}
      className={className}
    />
  )
}
